import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { NextSeo } from 'next-seo';
import _ from 'lodash';

import Footer from 'components/Footer';
import MaxWidthContainer from 'components/MaxWidthContainer';
import Menubar from 'components/Menubar';
import MobileTabBar from 'components/MobileTabBar';

import api from 'utils/api';
import { useAppLaunch } from 'contexts/AppLaunch';

const ALPHABET = ['#', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')];
const SAFETY_ORDER = ['安全', '一般', '風險', '其他/待確認'];

const SAFETY_COLOR_FALLBACK = {
  安全: '#0E9F6E',
  一般: '#9B9B9B',
  風險: '#F94C00',
  '其他/待確認': '#76777B',
};

const toSlug = (name) =>
  String(name || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-');

function FeatureBlock({ feature, safetyColorMap }) {
  const [activeLetter, setActiveLetter] = useState(null);

  const letterMap = useMemo(() => _.keyBy(feature.letters, 'letter'), [feature]);
  const activeData = activeLetter ? letterMap[activeLetter] : null;

  return (
    <section className="border-b border-gray-100 pb-8 last:border-b-0">
      <h2 className="mb-4 text-base font-medium text-[#4A4A4A] lg:text-lg">
        {feature.name}
      </h2>

      <div className="flex flex-wrap gap-x-3 gap-y-3">
        {ALPHABET.map((letter) => {
          const hasData = !!letterMap[letter];
          const isActive = activeLetter === letter;
          return (
            <button
              key={letter}
              type="button"
              disabled={!hasData}
              onClick={() =>
                setActiveLetter((prev) => (prev === letter ? null : letter))
              }
              className={
                'flex items-center gap-x-1 text-[18px] font-normal transition ' +
                (hasData
                  ? isActive
                    ? 'cursor-pointer text-sorra-pink'
                    : 'cursor-pointer text-sorra-gray-900 hover:text-sorra-pink'
                  : 'cursor-not-allowed text-[#9B9B9B] opacity-40')
              }
            >
              <img
                src="/svgs/index-partnership-arrow.svg"
                className={`h-6 w-6 ${isActive ? 'rotate-90 transform' : ''}`}
              />
              <span>{letter}</span>
            </button>
          );
        })}
      </div>

      {activeData && (
        <div className="mt-4 rounded-lg bg-[#F5F5F5] p-4 lg:p-6">
          {SAFETY_ORDER.map((safetyName) => {
            const group = (activeData.groups || []).find(
              (g) => g.safety === safetyName
            );
            if (!group || _.isEmpty(group.items)) return null;
            const color =
              safetyColorMap[safetyName] || SAFETY_COLOR_FALLBACK[safetyName];
            return (
              <div key={safetyName} className="mb-4 last:mb-0">
                <div className="mb-2 flex items-center gap-x-2">
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ background: color }}
                  />
                  <span className="text-sm text-[#4A4A4A]">{safetyName}</span>
                </div>
                <p className="text-[16px] leading-8 text-sorra-gray-900">
                  {group.items.map((item, idx) => (
                    <React.Fragment key={item.id}>
                      <Link
                        prefetch={false}
                        href={`/ingredients/${item.id}-${toSlug(item.name)}`}
                        className="underline underline-offset-2 hover:text-sorra-pink"
                      >
                        {item.name}
                      </Link>
                      {idx < group.items.length - 1 && (
                        <span className="px-2 text-sorra-gray-900">|</span>
                      )}
                    </React.Fragment>
                  ))}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default function IngredientSummary({ features, seo }) {
  const { notifications, ingredientSafeties } = useAppLaunch();

  const safetyColorMap = useMemo(
    () =>
      (ingredientSafeties || []).reduce((acc, s) => {
        if (s?.name) acc[s.name] = s.tag_color;
        return acc;
      }, {}),
    [ingredientSafeties]
  );

  return (
    <div>
      <NextSeo
        title={seo.title}
        description={seo.description}
        canonical="https://www.sorra.net/ingredientSummary"
      />
      <main>
        <Menubar props={{ homeExtend: false, sourceUI: 'IngredientSummary' }} />
        <div
          className={
            'globals-centerContentsMargin' +
            (notifications.length > 0 ? ' withNoti' : '')
          }
        >
          <MaxWidthContainer className="px-4">
            <div className="flex w-[100%] flex-wrap items-center gap-1 py-[16px] lg:py-[32px]">
              首頁 &gt; 成份彙總
            </div>
            <h1 className="pb-6 text-3xl font-bold text-[#111928] lg:pb-10 lg:text-4xl">
              成份彙總
            </h1>

            <div className="flex flex-col gap-y-8 pb-20">
              {(features || []).map((feature) => (
                <FeatureBlock
                  key={feature.id}
                  feature={feature}
                  safetyColorMap={safetyColorMap}
                />
              ))}
            </div>
          </MaxWidthContainer>
        </div>

        <Footer />
        <MobileTabBar props={{}} />
      </main>
    </div>
  );
}

export async function getServerSideProps() {
  let features = [];
  try {
    const { data } = await api.get('/api/seo/ingredients');
    features = Array.isArray(data) ? data : [];
  } catch (e) {
    features = [];
  }
  return {
    props: {
      features,
      seo: {
        title: '成份彙總｜美妝護膚品成份分類與安全性查詢 | Sorra 香港',
        description:
          '依功能、字母及安全性分類整理化妝品及護膚品成份，快速查找成份名稱、安全性與用途。',
      },
    },
  };
}
